import { Alert, Badge, Box, Card, Group, Pagination, Progress, Select, Skeleton, Stack, Table, Text, TextInput } from "@mantine/core";
import { IconAlertTriangle, IconSearch, IconUsersGroup } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { CustomerInfoApi } from "../../../apis";
import type { CustomerListRow } from "../../../apis/customerInfo";
import { EmptyState } from "../../../components/billing/EmptyState";
import { PageLayout } from "../../../components/layout";

const PAGE_SIZES = ["20", "50", "100", "200"];


const completenessColor = (p: number): string => (p >= 80 ? "green" : p >= 50 ? "yellow" : "red");

const TypeBadges = ({ row }: { row: CustomerListRow }) => (
  <Group gap={6}>
    {row.isBuyer && <Badge color="brand" size="sm" variant="light">Худалдан авагч</Badge>}
    {row.isSupplier && <Badge color="grape" size="sm" variant="light">Нийлүүлэгч</Badge>}
    {!row.isBuyer && !row.isSupplier && <Text size="sm" c="dimmed">—</Text>}
  </Group>
);

const CustomerInfoList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const { data, error, isLoading } = useSWR(
    ["swr.my.customer-info", page, limit, debouncedSearch] as const,
    () => CustomerInfoApi.listCustomers({ page, limit, search: debouncedSearch || undefined })
  );

  const rows: CustomerListRow[] = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  return (
    <PageLayout title="Харилцагчийн мэдээлэл" subTitle="Танай байгууллагатай холбоотой харилцагчид ба тэдний мэдээллийн бүрдэл">
      <Stack gap="md">
        <Alert color="brand" variant="light" icon={<IconUsersGroup size={16} />}>
          <Text span fw={700}>Худалдан авагч ба нийлүүлэгч харилцагчдын онбординг мэдээлэл.</Text>{" "}
          <Text span c="dimmed">Дэлгэрэнгүй харахын тулд мөр дээр дарна уу.</Text>
        </Alert>

        <Card withBorder radius="md" padding="md">
          <Stack gap="md">
            <Group gap="sm" wrap="wrap">
              <TextInput
                placeholder="Нэр эсвэл регистрээр хайх"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                leftSection={<IconSearch size={16} />}
                w={320}
              />
              <Box ml="auto">
                <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>{total} харилцагч</Text>
              </Box>
            </Group>

            {error ? (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>Мэдээлэл ачааллахад алдаа гарлаа.</Alert>
            ) : isLoading ? (
              <Skeleton height={240} radius="md" />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<IconUsersGroup size={26} />}
                message={debouncedSearch ? "Хайлтад тохирох харилцагч олдсонгүй." : "Харилцагч одоогоор алга."}
              />
            ) : (
              <Table.ScrollContainer minWidth={720}>
                <Table verticalSpacing="sm" highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Байгууллагын нэр</Table.Th>
                      <Table.Th>Төрөл</Table.Th>
                      <Table.Th>Байгууллагын админ</Table.Th>
                      <Table.Th style={{ width: 200 }}>Бөглөлт</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((r) => (
                      <Table.Tr
                        key={r.partnerId}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/my-billing/customer-info/${r.partnerId}`)}
                      >
                        <Table.Td>
                          <Stack gap={0}>
                            <Text size="sm" fw={600}>{r.businessName ?? "(нэргүй)"}</Text>
                            <Text size="xs" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                              {r.regNumber ?? "—"}
                            </Text>
                          </Stack>
                        </Table.Td>
                        <Table.Td><TypeBadges row={r} /></Table.Td>
                        <Table.Td>
                          <Text size="sm" c={r.adminName ? undefined : "dimmed"}>{r.adminName ?? "—"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            <Progress
                              value={r.completenessPercent}
                              color={completenessColor(r.completenessPercent)}
                              w={110}
                              radius="xl"
                              size="md"
                            />
                            <Text size="sm" fw={600} w={44} style={{ fontVariantNumeric: "tabular-nums" }}>
                              {r.completenessPercent}%
                            </Text>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}

            {total > 0 && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {from}-{to} / {total}
                </Text>
                <Group gap="sm">
                  <Pagination value={page} onChange={setPage} total={pageCount} size="sm" hideWithOnePage />
                  <Select
                    value={`${limit}`}
                    onChange={(v) => {
                      if (v !== null) {
                        setLimit(parseInt(v, 10));
                        setPage(1);
                      }
                    }}
                    data={PAGE_SIZES}
                    w={100}
                    size="xs"
                  />
                </Group>
              </Group>
            )}
          </Stack>
        </Card>
      </Stack>
    </PageLayout>
  );
};

export default CustomerInfoList;
